/**
 * Managed WebSocket — the shared connection lifecycle both monolith pages
 * previously hand-rolled (with independently-evolved bug fixes for the same
 * problems: stale-socket callbacks after a replace, stacked reconnect
 * timers, doomed connect attempts while logged out).
 *
 * Behaviors owned here, so every consumer gets them once and identically:
 *  - Auth-aware connect: `protocols()` returning null/undefined means "not
 *    logged in" — no socket is opened (the server would just deny the
 *    handshake and a reconnect loop would hammer it), `onAuthMissing` fires.
 *  - Staleness guard: callbacks only ever fire for the CURRENT socket.
 *    Replacing/closing a socket makes the old one's late events no-ops —
 *    the "two live WebSockets both applying ticks to the same chart" class.
 *  - Single reconnect timer: a new connect() or close() cancels any pending
 *    reconnect; a stale socket's close can never schedule a second loop.
 *  - JSON parse guard: onMessage receives parsed objects only.
 *
 * The returned handle intentionally satisfies the little contract the pages
 * already rely on for raw sockets: `.close()` and `.readyState`.
 */

export interface WebSocketLike {
  onopen: (() => void) | null;
  onclose: (() => void) | null;
  onerror: ((err: unknown) => void) | null;
  onmessage: ((ev: { data: string }) => void) | null;
  readyState: number;
  close(): void;
}

// Numeric WebSocket readyState constants (avoid depending on a global).
export const WS_OPEN = 1;
export const WS_CLOSED = 3;

export interface ManagedSocketOpts {
  url: () => string;
  /** Auth subprotocols; null/undefined = not logged in, don't connect. */
  protocols: () => string[] | null | undefined;
  onMessage: (msg: any) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onError?: (err: unknown) => void;
  onAuthMissing?: () => void;
  /** null/undefined = no auto-reconnect. */
  reconnectDelayMs?: number | null;
  shouldReconnect?: () => boolean;
  label?: string;
  /** Test seam; defaults to the browser WebSocket. */
  wsFactory?: (url: string, protocols: string[]) => WebSocketLike;
}

export interface ManagedSocket {
  /** (Re)connect now — cancels any pending reconnect, closes the previous socket. */
  connect(): void;
  /** Permanent close — cancels reconnects; the handle stays CLOSED forever. */
  close(): void;
  isOpen(): boolean;
  raw(): WebSocketLike | null;
  readonly readyState: number;
}

export function managedSocket(opts: ManagedSocketOpts): ManagedSocket {
  const label = opts.label ?? "ws";
  const factory =
    opts.wsFactory ??
    ((url: string, protocols: string[]) =>
      new (globalThis as any).WebSocket(url, protocols) as WebSocketLike);

  let current: WebSocketLike | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let closedForever = false;

  function cancelTimer(): void {
    if (reconnectTimer != null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function detachAndClose(ws: WebSocketLike | null): void {
    if (!ws) return;
    // Null the handlers FIRST: a close() below may fire onclose synchronously
    // in some implementations, and a detached socket must be fully inert.
    ws.onopen = ws.onclose = ws.onerror = ws.onmessage = null;
    try {
      ws.close();
    } catch {
      /* already dead */
    }
  }

  function scheduleReconnect(): void {
    if (closedForever) return;
    if (opts.reconnectDelayMs == null) return;
    if (opts.shouldReconnect && !opts.shouldReconnect()) return;
    cancelTimer(); // never two pending loops
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      connect();
    }, opts.reconnectDelayMs);
  }

  function connect(): void {
    if (closedForever) return;
    cancelTimer();
    detachAndClose(current);
    current = null;

    const protocols = opts.protocols();
    if (!protocols) {
      opts.onAuthMissing?.();
      return;
    }

    const ws = factory(opts.url(), protocols);
    current = ws;

    ws.onopen = () => {
      if (ws !== current) return;
      opts.onOpen?.();
    };
    ws.onerror = (err) => {
      if (ws !== current) return;
      opts.onError?.(err);
    };
    ws.onclose = () => {
      if (ws !== current) return;
      current = null;
      opts.onClose?.();
      scheduleReconnect();
    };
    ws.onmessage = (ev) => {
      if (ws !== current) return;
      let msg: any;
      try {
        msg = JSON.parse(ev.data);
      } catch (e) {
        console.error(`[${label}] WS parse error`, e);
        return;
      }
      opts.onMessage(msg);
    };
  }

  const handle: ManagedSocket = {
    connect,
    close(): void {
      closedForever = true;
      cancelTimer();
      const ws = current;
      current = null;
      detachAndClose(ws);
    },
    isOpen: () => current != null && current.readyState === WS_OPEN,
    raw: () => current,
    get readyState(): number {
      return current ? current.readyState : WS_CLOSED;
    },
  };

  connect();
  return handle;
}
